import { useUnit } from 'effector-react';
import _, { get, identity, pickBy } from 'lodash';
import { $global, storage } from 'models/store-global';
import React, { useEffect, useMemo, useRef } from 'react';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import styled, { css, keyframes } from 'styled-components';

import HText from 'components/HText';
import Dropdown from 'components/StyledTemplates/Dropdown';
import SortArrow from 'components/StyledTemplates/SortArrow';
import { Button, Checkbox, Frame, Row, RowWrapper, TExtra } from 'components/StyledTemplates/StyledTemplates';
import Tooltip from 'components/StyledTemplates/Tooltip';

import { getElementParentsPath, objectFlatten, stringImposition, togglePush } from 'utils/common-helper';

import useDebounce from 'hooks/useDebounce';
import usePagination, { type PaginationOptions } from 'hooks/usePagination';
import useResources from 'hooks/useResources';

import imgIconsMoreVertDark from 'assets/icons/more-vert-dark.svg';
import imgIconsPaginationChevronLeft from 'assets/icons/pagination-chevron-left.svg';

import { MyStore, SORT_ORDERS, TableState } from 'types/Store';

export type ColumnProps<T> = {
    name: string;
    label?: string;
    sortable?: boolean;
    extra?: string;
    transform?: (value: TableCellProps<T>['cellState']) => any;
    skipTransformBeforeSort?: boolean;
    onCellClick?: (value: T) => void;
    tooltip?: (value: TableCellProps<T>['cellState']) => any | string;
    cell?: {
        type?: 'text' | 'icon' | 'menu' | 'custom';
        extra?: string;
        customContent?: (row: T) => React.ReactNode;
        src?: any;
        options?: (cellState: TableCellProps<T>['cellState']) => {
            muted?: boolean;
            extra?: TExtra;
            onClick?: () => void;
            children?: any;
        }[];
    } & Partial<IconProps>;
};

export type TableProps<T extends { [key in IdColumnName | string]: unknown }, IdColumnName extends keyof T> = {
    name: string;
    columns: ColumnProps<T>[];
    rows?: T[];
    rowsNumber?: number;
    extra?: TExtra;
    selectable?: boolean;
    sortable?: boolean;
    selectionLimit?: number;
    withPagination?: boolean;
    withHeader?: boolean;
    paginationOptions?: number[];
    defaultSort?: SORT_ORDERS;
    idColumnName: IdColumnName;
    dependencies?: any;
    tableStyles?: TExtra;
    paginationStyles?: TExtra;
    extraHeader?: React.ReactNode;
    filters?: Record<string, unknown>;
    booleanOperation?: 'conjunction' | 'disjunction';
    ignoreInitFetch?: boolean;
    onRowClick?: {
        callback: (row: T) => void;
        disabled?: boolean | ((row: T) => boolean);
    };
    highlightRowIds?: T[IdColumnName][];
};

function Table<T extends Record<string | IdColumnName, unknown>, IdColumnName extends keyof T>(
    props: TableProps<T, IdColumnName>,
) {
    const {
        extra = ``,
        selectable = false,
        name,
        sortable = false,
        columns = [],
        selectionLimit = Infinity,
        withPagination = true,
        withHeader = true,
        paginationOptions = [10, 25, 50, 100, 500],
        defaultSort,
        idColumnName = 'id' as IdColumnName,
        dependencies = {},
        tableStyles = '',
        extraHeader,
        rows: propRows = [],
        rowsNumber,
        filters: propsFilters = {},
        booleanOperation = 'conjunction',
        onRowClick,
        highlightRowIds = [],
    } = props;

    const navigate = useNavigate();

    const tableState = useUnit($global.map((state) => state.tables?.[name]));

    const { rows = [], selectedRows = [], filters = propsFilters } = tableState || {};

    const setSelectedRows = (newValue: T[IdColumnName][] = []) => {
        storage.set(
            (store): MyStore => ({
                ...store,
                tables: {
                    ...(store.tables || {}),
                    [name]: {
                        ...(store.tables?.[name] || {}),
                        selectedRows: newValue.slice(0, selectionLimit),
                    } as TableState,
                },
            }),
        );
    };
    const sort = tableState?.sort?.[0] ?? defaultSort;
    const setSort = (field: any, order: SORT_ORDERS) => {
        storage.set(
            (store): MyStore => ({
                ...store,
                tables: {
                    ...(store.tables || {}),
                    [name]: {
                        ...(store.tables?.[name] || {}),
                        sort: [{ field, order }],
                    } as TableState,
                },
            }),
        );
    };
    const filterRows = (rows: T[]): T[] =>
        rows?.filter?.((row) => {
            return (
                Object.keys(pickBy(objectFlatten(filters ?? {}), identity)).length === 0 ||
                Object.entries(pickBy(objectFlatten(filters ?? {}), identity))
                    ?.map?.(([key, value]) => !value || stringImposition(`${get(row, key)}`, value))
                    ?.reduce?.(
                        // @ts-ignore
                        ...{
                            conjunction: [(a: any, b: any) => a && b, true],
                            disjunction: [(a: any, b: any) => a || b, false],
                        }[booleanOperation],
                    )
            );
        });
    let sortedPropRows = propRows;
    if (tableState?.sort?.[0]) {
        const column = columns.find((c) => c.name === tableState.sort?.[0].field);
        sortedPropRows = _.orderBy(
            sortedPropRows,
            [
                (row) => {
                    let value = _.get(row, tableState.sort?.[0].field);
                    if (column?.transform && !column?.skipTransformBeforeSort) {
                        value = column.transform({
                            row: row,
                            column: column,
                            value: value,
                        } as any);
                    }
                    if (typeof value === 'string') {
                        return value.toLowerCase();
                    }
                    return value;
                },
            ],
            tableState.sort?.[0].order,
        );
    }
    if (rowsNumber && sortedPropRows.length > rowsNumber) {
        sortedPropRows = sortedPropRows.slice(0, rowsNumber);
    }
    const frontendPagination = usePagination(filterRows(sortedPropRows), { perPage: 10 });
    const pagination = frontendPagination ?? {};
    const { currentPage = 0, perPage = paginationOptions?.[0] ?? 1, pagesCount = 1, totalCount = 1 } = pagination;
    const maxPageNumber = Math.ceil(totalCount / perPage);
    const debouncedParams = useDebounce(JSON.stringify({ filters }), 300);
    useEffect(() => {
        (async () => {
            if (!tableState?.sort) {
                storage.set(
                    (store): MyStore => ({
                        ...store,
                        tables: {
                            ...(store.tables || {}),
                            [name]: {
                                ...(store.tables?.[name] || {}),
                                sort: [{ field: 'id', order: defaultSort }],
                            } as TableState,
                        },
                    }),
                );
            }
            if (!tableState?.pagination) {
                storage.set(
                    (store): MyStore => ({
                        ...store,
                        tables: {
                            ...(store.tables || {}),
                            [name]: {
                                ...(store.tables?.[name] || {}),
                                pagination: { currentPage, perPage },
                            } as TableState,
                        },
                    }),
                );
            }
        })();
    }, [JSON.stringify({ debouncedParams, sort, name, currentPage, perPage, dependencies })]);

    return (
        <TableWrapper extra={extra}>
            {extraHeader}
            <table>
                <STable extra={tableStyles}>
                    {withHeader && (
                        <STr
                            extra={({ theme }) =>
                                `border-bottom: 0px; padding: 16px; box-sizing: border-box; border-radius: 6px 6px 0px 0px; * { color: ${theme.secondary}; };`
                            }
                        >
                            {selectable && <STh extra="flex: unset; width: 40px;"></STh>}
                            {columns.map((column, index) => {
                                return (
                                    <STh key={index} extra={column.extra}>
                                        <Frame
                                            extra="flex-direction: row; cursor: pointer; font-weight: normal;"
                                            onClick={() => {
                                                if (sortable && column.sortable !== false) {
                                                    setSort(
                                                        column.name,
                                                        typeof sort === 'object' && column.name === sort?.field
                                                            ? (
                                                                  {
                                                                      asc: 'desc',
                                                                      desc: 'asc',
                                                                  } as const
                                                              )[sort?.order] || ('desc' as const)
                                                            : ('desc' as const),
                                                    );
                                                }
                                            }}
                                        >
                                            {column.label}
                                            {sortable && column.sortable !== false && (
                                                <SortArrow
                                                    order={
                                                        tableState?.sort?.find?.((i: any) => i?.field === column?.name)
                                                            ?.order
                                                    }
                                                />
                                            )}
                                        </Frame>
                                    </STh>
                                );
                            })}
                        </STr>
                    )}
                    {filterRows(
                        withPagination
                            ? frontendPagination.visibleItems ?? []
                            : rows.length === 0 && propRows
                            ? propRows
                            : rows,
                    )?.length === 0 && (
                        <tr>
                            <td style={{ justifyContent: 'center', display: 'flex' }}>
                                <PlaceholderNoData>No data</PlaceholderNoData>
                            </td>
                        </tr>
                    )}
                    {filterRows(
                        withPagination
                            ? frontendPagination.visibleItems ?? []
                            : rows.length === 0 && sortedPropRows
                            ? sortedPropRows
                            : rows,
                    )?.map?.((row, row_index) => {
                        const isDisabledRowClick =
                            onRowClick === undefined || _.isEmpty(onRowClick)
                                ? true
                                : _.isBoolean(onRowClick?.disabled)
                                ? onRowClick.disabled !== false
                                : _.isFunction(onRowClick?.disabled)
                                ? onRowClick.disabled(row) !== false
                                : false;
                        return (
                            <STr
                                key={row[idColumnName] as number | string | null | undefined}
                                extra={`${row_index === 0 ? `border-top: 1px solid #DEE2E6;` : ``}${
                                    !isDisabledRowClick
                                        ? 'cursor: pointer; &:hover { > * { opacity: 0.75; }; > *:last-child { opacity: 1; } };'
                                        : ''
                                }`}
                                onClick={(e: any) => {
                                    const ignoreClick = !getElementParentsPath(e.target)
                                        .filter((i) => !!i)
                                        .map((i) => i.classList?.value?.includes?.('ignore-row-click'))
                                        .includes(true);
                                    if (onRowClick === undefined || _.isEmpty(onRowClick)) {
                                        return;
                                    }
                                    if (!isDisabledRowClick && ignoreClick) {
                                        onRowClick.callback(row);
                                    }
                                }}
                                isHighlighted={highlightRowIds?.includes?.(row[idColumnName])}
                            >
                                {selectable && (
                                    <STd extra="flex: unset; width: 40px;">
                                        <Checkbox
                                            disabled={
                                                !selectedRows.includes(row[idColumnName]) &&
                                                selectedRows.length === selectionLimit
                                            }
                                            checked={selectedRows.includes(row[idColumnName])}
                                            onChange={() =>
                                                setSelectedRows(togglePush(selectedRows, row[idColumnName]))
                                            }
                                        />
                                    </STd>
                                )}
                                {columns.map((column, column_index) => {
                                    const cellState = { row, column, value: _.get(row, column.name), navigate };
                                    return (
                                        <TableCell
                                            row_index={row_index}
                                            column_index={column_index}
                                            key={column_index}
                                            cellState={cellState}
                                        />
                                    );
                                })}
                            </STr>
                        );
                    })}
                </STable>
            </table>
            {withPagination && <Pagination.Component {...pagination} />}
        </TableWrapper>
    );
}

const Pagination = {
    Component: (props: PaginationOptions & { windowSize?: number }) => {
        const {
            windowSize = 3,
            perPage,
            totalCount,
            currentPage,
            handlePageNavigation,
            handlePrevPage,
            handleNextPage,
            handlePerPageChange,
        } = props;

        const { t } = useResources();

        if (windowSize % 2 === 0) {
            throw new Error(`Table.js: Pagination windowSize must be odd`);
        }
        const ratio = Math.floor(windowSize / 2);

        const selectRef = useRef();

        return (
            <Pagination.Wrapper>
                <Row extra="gap: 5px; font-weight: 500;">
                    {t('table.pagination.showing')}:{' '}
                    <Pagination.Select
                        ref={selectRef}
                        value={perPage}
                        onChange={(e: any) => handlePerPageChange(e.target.value)}
                    >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                    </Pagination.Select>
                    {perPage <= totalCount && (
                        <>
                            {t('table.pagination.of')} <strong>{totalCount}</strong>
                        </>
                    )}
                </Row>
                <Row extra="gap: 10px;">
                    <Pagination.Arrow direction="left" onClick={handlePrevPage} />
                    {_.range(Math.ceil(totalCount / perPage))
                        .filter(
                            (_i, j, self) =>
                                j >= currentPage - ratio - Math.max(0, currentPage - self.length + (ratio + 1)) &&
                                j <= currentPage + ratio - Math.min(0, currentPage - ratio),
                        )
                        .map((i: number) => (
                            <Pagination.PageNumber
                                key={i}
                                selected={currentPage === i}
                                onClick={() => handlePageNavigation(i)}
                            >
                                {i + 1}
                            </Pagination.PageNumber>
                        ))}
                    <Pagination.Arrow direction="right" onClick={handleNextPage} />
                </Row>
            </Pagination.Wrapper>
        );
    },
    Select: styled.select<{ ref?: any }>`
        border: unset;
        color: ${({ theme }) => theme.primary};
        font-weight: 700;
        cursor: pointer;

        -webkit-appearance: auto;

        &:before {
            content: '';
        }
    `,
    Wrapper: styled(RowWrapper)`
        padding: 15px 20px;
        box-sizing: border-box;
    `,
    Arrow: styled(Frame)<{ direction: 'left' | 'right' | 'down' | 'up' }>`
        width: 5px;
        height: 10px;
        background: url('${imgIconsPaginationChevronLeft}') no-repeat center center / contain;
        transform: rotate(${({ direction }) => ({ left: 0, right: 180, up: 90, down: -90 }?.[direction])}deg);
        cursor: pointer;
    `,
    PageNumber: styled(Frame)<{ selected: boolean }>`
        width: 20px;
        height: 20px;
        border-radius: 4px;
        font-weight: 500;
        font-size: 13px;
        line-height: 24px;
        cursor: pointer;

        ${({ selected }) =>
            selected &&
            css`
                background: ${({ theme }) => theme.primary};
                color: white;
            `}
    `,
};

const smartStringify = (value: any) => {
    return !!value && !!`${value}` ? (typeof value !== `string` ? JSON.stringify(value) : value) : `-`;
};

interface TableCellProps<T> {
    cellState: {
        row: T;
        column: ColumnProps<T>;
        value: any;
        navigate: NavigateFunction;
    };
    column_index: number;
    row_index: number;
}

const TableCell = ({ cellState }: TableCellProps<any>) => {
    const { row, column, value } = cellState;
    const transformedValue = smartStringify(column?.transform?.(cellState) ?? value ?? _.get(row, column.name));
    const cellContent = useMemo(() => {
        return (
            {
                text: <HText>{`${transformedValue}`}</HText>,
                // @ts-ignore
                icon: <Icon {..._.pick(column.cell, ['src', 'extra'])} />,
                menu: (
                    <Dropdown
                        className="ignore-row-click"
                        toggle={<Icon src={imgIconsMoreVertDark} />}
                        menu={
                            <>
                                {column?.cell?.options?.(cellState)?.map?.((option, index) => (
                                    <Option key={index} {...option} />
                                ))}
                            </>
                        }
                    />
                ),
                custom: column?.cell?.customContent ? column?.cell?.customContent(row) : null,
            }?.[column?.cell?.type ?? 'text'] ?? ``
        );
    }, [JSON.stringify(cellState, (_key, val) => (typeof val === 'function' ? Math.random() : val))]);

    const tooltip = _.isFunction(column?.tooltip) ? column?.tooltip(cellState) : column?.tooltip;
    return (
        <STd
            extra={(column.extra ?? '') + (column.cell?.extra ?? '')}
            clickable={!!column.onCellClick}
            onClick={() => column?.onCellClick?.(cellState)}
        >
            {tooltip ? (
                <Tooltip
                    {...tooltip}
                    children={cellContent}
                    tooltipProps={{
                        extra: `max-width: 350px; width: max-content; white-space: break-spaces;`,
                    }}
                />
            ) : (
                cellContent
            )}
        </STd>
    );
};

// prettier-ignore
export const Option = styled(Button).attrs((props) => { return { ...props, variant: 'light' }; })<{ disabled?: boolean; label?: string; extra?: TExtra; 'data-qaid'?: string }>`
    width: 175px;
    padding: 8px;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    cursor: default;
    opacity: 0.5 !important;
    font-weight: 500;
    text-align: start;

    ${({ disabled = false }) =>
        !disabled &&
        css`
            cursor: pointer;
            opacity: 1 !important;
        `}
    ${({ disabled = false }) =>
        !disabled &&
        css`
            &:hover {
                &:after {
                    transform: translate(2px, 0);
                }
            }
        `}
    transition: 0.2s;

    ${({ extra }) => extra}
`;

const PlaceholderNoData = styled(Frame)`
    color: ${(props) => props.theme.grey};
    font-size: 16px;
    padding: 40px 0;
`;

interface IconProps {
    src: string;
    extra?: TExtra;
}

const Icon = styled(Frame)<IconProps>`
    width: 20px;
    height: 20px;
    background: url('${({ src }) => src}') no-repeat center center / contain;
    cursor: pointer;

    ${({ extra }) => extra}
`;

const TableWrapper = styled(Frame)`
    width: 100%;
    /* height: 100%; */
    /* background: ${({ theme }) => theme.background.primary}; */
    /* border: 1px solid #dadada; */
    border-radius: 6px;
    box-sizing: border-box;
    font-style: normal;
    font-weight: normal;

    table {
        display: flex;
        width: 100%;
    }
`;

const STd = styled.td<{ extra?: TExtra; clickable?: boolean }>`
    display: flex;
    padding: 4px;
    justify-content: flex-start;
    width: 100%;
    flex: 1;
    flex-direction: row;

    /* overflow: hidden; */
    white-space: break-spaces;

    ${({ clickable }) =>
        clickable &&
        css`
            cursor: pointer;

            &:hover {
                opacity: 0.5;
            }
        `}

    ${({ extra }) => extra}
`;

const STh = styled.th<{ extra?: TExtra }>`
    padding: 4px;
    justify-content: flex-start;
    width: 100%;
    flex: 1;
    flex-direction: row;
    font-weight: bold;
    font-size: 14px;
    line-height: 20px;
    display: flex;
    align-items: center;

    ${({ extra }) => extra}
`;

const highlightAnimation = (color: string) => keyframes`
    0% { background-color: ${color}20; }
    20% { background-color: transparent; }
    40% { background-color: ${color}20; }
    60% { background-color: transparent; }
    80% { background-color: ${color}20; }
    100% { background-color: transparent; }
`;

const STr = styled.tr<{ extra?: TExtra; isHighlighted?: boolean }>`
    display: flex;
    height: max-content;
    flex-direction: row;
    width: 100%;
    justify-content: space-between;
    padding: 20px;
    box-sizing: border-box;
    border-bottom: 1px solid #dee2e6;
    white-space: nowrap;
    align-items: center;

    > * {
        word-break: break-word;
        font-weight: 600;
    }

    ${({ isHighlighted }) =>
        isHighlighted &&
        css`
            animation: ${({ theme }) => highlightAnimation(theme.primary)} 2s ease-in-out;
        `}

    ${({ extra }) => extra}
`;

const STable = styled.tbody<{ extra?: TExtra }>`
    width: 100%;
    display: flex;
    flex-direction: column;
    border-collapse: collapse;

    ${({ extra }) => extra}
`;

export default Table;
